import type { App } from 'vue';

import type { AxiosInstance } from 'axios';
import axiosLib from 'axios';
import { tracing } from 'utilities';

import qs from 'qs';
import CookieService from '~/services/CookieService';
import LocalStorage from '~/services/LocalStorage';
import BaseURLS from '~/config/BaseURLs';
import { getEnvironmentEnv, isE2ETest, isJenkinsTest, isProd, isSSRHydration, isServer } from '~/utils/EnvUtils';
import useSocketMetaStore from '~/stores/socketMetaStore';

import $store from '~/services/StoreService';
import QLogger from '~/services/QLogger';

import https from 'node:https';
import http from 'node:http';

const axios = isServer()
  ? axiosLib.create({
    httpsAgent: new https.Agent({ keepAlive: true }),
    httpAgent: new http.Agent({ keepAlive: true }),
  })
  : axiosLib;

// interceptors
function addDevAuthInterceptors(axios: AxiosInstance) {
  axios.interceptors.request.use(
    (config) => {
      if (isProd()) {
        return config;
      }

      const hostName = new URL(config.url || '', BaseURLS.API_SERVER[getEnvironmentEnv()]).hostname;
      if (BaseURLS.SPECIAL_HOSTNAME[getEnvironmentEnv()].findIndex(h => hostName.includes(h)) >= 0) {
        config.withCredentials = false;
        return config;
      }
      config.withCredentials = true;
      const authCookie = CookieService.get('dev_sid');
      if (authCookie) {
        config.headers['x-auth-sessionid'] = authCookie;
      }
      return config;
    },
    error => Promise.reject(error),
  );
}

function includeCredentials(axios: AxiosInstance) {
  axios.interceptors.request.use(
    (config) => {
      if (config?.url?.includes('play-api')) {
        const socketMetaStore = useSocketMetaStore();
        const handshakeData = socketMetaStore.handshakeData;
        config.data = { ...config.data, ...handshakeData };
      }
      return config;
    },
    error => Promise.reject(error),
  );
}

function addRequestContextTokenInterceptors(axios: AxiosInstance) {
  axios.interceptors.response.use(
    (response) => {
      const xRequestContextToken = response.headers['x-q-request-context-token'];
      if (xRequestContextToken) {
        LocalStorage.setItem(
          'x-q-request-context-token',
          xRequestContextToken,
          { storeAsJSON: false },
        );
      }
      return response;
    },
    error => Promise.reject(error),
  );

  axios.interceptors.request.use(
    (config) => {
      const hostName = new URL(config.url || '', BaseURLS.API_SERVER[getEnvironmentEnv()]).hostname;
      if (BaseURLS.SPECIAL_HOSTNAME[getEnvironmentEnv()].findIndex(h => hostName.includes(h)) >= 0) {
        config.withCredentials = false;
        return config;
      }
      config.withCredentials = true;
      const xRequestContextToken = LocalStorage.getItem(
        'x-q-request-context-token',
        { parseFromJSON: false, removeItemOnError: false },
      );
      if (xRequestContextToken) {
        config.headers['x-q-request-context-token'] = xRequestContextToken;
      }
      return config;
    },
    error => Promise.reject(error),
  );
}

function addTracingInterceptors(axios: AxiosInstance) {
  axios.interceptors.request.use(
    (config) => {
      const hostName = new URL(config.url || '', BaseURLS.API_SERVER[getEnvironmentEnv()]).hostname;
      if (BaseURLS.SPECIAL_HOSTNAME[getEnvironmentEnv()].findIndex(h => hostName.includes(h)) >= 0) {
        config.withCredentials = false;
        return config;
      }
      const traceId = tracing.getTraceId();
      config.headers['x-q-traceid'] = traceId;
      config.headers['X-Amzn-Trace-Id'] = traceId;

      return config;
    },
  );
}

// Temporary code to find the root cause of test failures
function addE2ETestInterceptors(axios: AxiosInstance) {
  axios.interceptors.request.use(
    (config) => {
      if (isE2ETest() || isJenkinsTest()) {
        try {
          config.headers['x-q-e2e-testid'] = `${isJenkinsTest() ? 'jenkins' : 'github'}:${window['e2e-testid']}`;
        } catch (e) {}
      }
      return config;
    },
  );
}

function addQuizServiceBEInterceptors(axios: AxiosInstance) {
  axios.interceptors.request.use(
    (config) => {
      if (config.headers['use-quiz-service-be']) {
        delete config.headers['use-quiz-service-be'];
        // replace the base URL
        config.baseURL = (axios.defaults as any).quizServiceURL;
        // prepend v2 to the url
        config.url = `/v2${!config.url?.startsWith('/') ? '/' : ''}${config.url}`;
        // add component type header - this is how quiz service knows the call was made from admin
        config.headers['x-component-type'] = 'adminv3';
      }
      return config;
    },
    error => Promise.reject(error),
  );
}

// call this to add common headers for prod and dev. if not needed, we will delete this

function addRequestInterceptors(axios: AxiosInstance) {
  axios.interceptors.request.use(
    (config) => {
      if (isServer() || isSSRHydration()) {
        delete config.headers['x-adminv3ssr_redirect'];
        delete config.headers['x-adminv3ssr_user_redirect'];
      }
      return config;
    },
    error => Promise.reject(error),
  );
}

// call this to add common headers for prod and dev
function addResponseInterceptors(axios: AxiosInstance) {
  axios.interceptors.response.use(
    (response) => {
      // console.log('axios response', `${response.request.path} [${response.status}]`);
      return response;
    },
    (error) => {
      QLogger.log('log error in response', error);
      // add sentry logger here
      return Promise.reject(error);
    },
  );
}

export function updateURLs(params?: { bypassRateLimiterKey?: string }) {
  const store = $store();
  const bypassRateLimiterKey = params?.bypassRateLimiterKey;
  if (bypassRateLimiterKey || store.getters['root/serverData'].bypassRateLimiterKey) {
    axios.defaults.params = {
      bypassRateLimiterKey: bypassRateLimiterKey ?? store.getters['root/serverData'].bypassRateLimiterKey,
    };
  }
  axios.defaults.baseURL = store.getters['root/serverData'].fullAPIPrefix || BaseURLS.API_SERVER[getEnvironmentEnv()];
  (axios.defaults as any).quizServiceURL = BaseURLS.QUIZ_SERVICE[getEnvironmentEnv()];
  (axios.defaults as any).mediaServiceURL = BaseURLS.MEDIA_SERVICE[getEnvironmentEnv()];
}

// plugin

export default {
  install: (app: App<Element>) => {
    axios.defaults.baseURL = BaseURLS.API_SERVER[getEnvironmentEnv()];
    (axios.defaults as any).quizServiceURL = BaseURLS.QUIZ_SERVICE[getEnvironmentEnv()];
    axios.defaults.paramsSerializer = (params) => {
      if (typeof params === 'object') {
        if (params.disabledSerialization) {
          return qs.stringify(params, { encode: false });
        }
        const keys = Object.keys(params);
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          const value = params[key];
          if (typeof value === 'object') {
            params[key] = JSON.stringify(value);
          }
        }
      }
      return qs.stringify(params, { encode: true });
    };

    app.config.globalProperties.$axios = axios;

    if (!isProd()) {
      axios.defaults.baseURL = BaseURLS.API_SERVER[getEnvironmentEnv()];
      (axios.defaults as any).quizServiceURL = BaseURLS.QUIZ_SERVICE[getEnvironmentEnv()];
      (axios.defaults as any).mediaServiceURL = BaseURLS.MEDIA_SERVICE[getEnvironmentEnv()];
      // Don't add interceptors here, consequences can be memory leaks on server
    }
  },
};

// Adding interceptors here instead of plugin install since Axios is being used as a singleton and it causes duplicate interceptors in SSR where same axios instance is used across requests
addRequestContextTokenInterceptors(axios);
addTracingInterceptors(axios);
includeCredentials(axios);

addDevAuthInterceptors(axios);

addQuizServiceBEInterceptors(axios);

addRequestInterceptors(axios);
addResponseInterceptors(axios);
addE2ETestInterceptors(axios);

export { axios };
